/**
 * ExportService.js
 * Service for handling the export and PDF generation functionality
 */
import { ENERGY_LINE_CONFIG } from './StyleService';
import { getTransposedChords } from './ChordService';
import { getAllSheets, getSheetById } from './SheetStorageService';
import { fetchWithAuth, API_URL } from './ApiService';
import { isAuthenticated as checkAuth } from '../utils/AuthUtils';
import logger from './LoggingService';

/**
 * Create a parts summary for collapsed sections
 * @param {Array} parts - Array of parts in the section
 * @returns {string} - Comma-separated list of parts without bar notation
 */
const createPartsSummary = (parts) => {
  return parts.map(part => {
    const partLabel = part.part || '';
    return partLabel;
  }).filter(Boolean).join(', ');
};

/**
 * Get the first line of text from a section's parts
 * @param {Array} parts - Array of parts in the section
 * @returns {string} - First non-empty line of lyrics
 */
const getFirstTextLine = (parts) => {
  for (const part of parts) {
    if (part.lyrics && part.lyrics.trim()) {
      const lines = part.lyrics.trim().split('\n');
      const firstLine = lines[0].trim();
      if (firstLine) {
        return firstLine;
      }
    }
  }
  return '';
};

/**
 * Get bars for a single part from partsModule
 * @param {string} partLabel - The part label (A, B, C, etc.)
 * @param {Array} partsModule - Parts module with bar data
 * @returns {number} - Number of bars for this part
 */
/**
 * Get chords for a single part from partsModule
 * @param {string} partLabel - The part label (A, B, C, etc.)
 * @param {Array} partsModule - Parts module with chord data
 * @returns {string} - Chords for this part
 */
const getChordsForPart = (partLabel, partsModule = []) => {
  if (!partLabel || partLabel.trim() === '') return '';
  const moduleItem = partsModule.find(item => item.part === partLabel);
  return moduleItem ? (moduleItem.chords || '') : '';
};

const getBarsForPart = (partLabel, partsModule = []) => {
  if (!partLabel || partLabel.trim() === '') {
    return 4; // Default fallback
  }
  
  const moduleItem = partsModule.find(item => item.part === partLabel);
  return moduleItem ? moduleItem.bars : 4; // Default to 4 bars if not found
};

/**
 * Get total bars for a section
 * @param {Array} parts - Array of parts in the section
 * @param {Array} partsModule - Parts module with bar data
 * @returns {number} - Total number of bars
 */
const getTotalBars = (parts, partsModule = []) => {
  // Fixed: Now reads from partsModule instead of part.bars  
  return parts.reduce((total, part) => {
    const partLabel = part.part;
    if (!partLabel || partLabel.trim() === '') {
      return total;
    }
    
    const bars = getBarsForPart(partLabel, partsModule);
    return total + (parseInt(bars) || 0);
  }, 0);
};

/**
 * Get combined notes from all parts in a section
 * @param {Array} parts - Array of parts in the section
 * @returns {string} - Combined notes
 */
const getCombinedNotes = (parts) => {
  const notes = parts
    .map(part => part.notes?.trim())
    .filter(Boolean)
    .join(' • ');
  return notes;
};

/**
 * Calculate energy line width for PDF export
 * @param {number} energyLevel - Energy level (1-10)
 * @returns {string} - CSS width value
 */
const getEnergyWidthForPdf = (energyLevel) => {
  // Use the shared configuration from StyleService
  const maxPercentage = ENERGY_LINE_CONFIG.MAX_WIDTH_PERCENTAGE;
  
  if (energyLevel === 1) {
    // For energy level 1, we want to match the section column width
    return '80px'; // Matches the new section column width
  } else if (energyLevel === 10) {
    return `${maxPercentage}%`;
  } else {
    // Linear interpolation between min width and max percentage
    // For PDF, we convert min width (80px) to a percentage (approx 8%)
    const minWidthAsPercentage = 8; // 80px is roughly 8% of sheet width
    const percentage = minWidthAsPercentage + ((maxPercentage - minWidthAsPercentage) / 9) * (energyLevel - 1);
    return `${percentage}%`;
  }
};

/**
 * Generate print-friendly HTML content
 * @param {Object} songData - Contains title, artist, bpm
 * @param {Array} sections - All sections with their parts for the sheet
 * @param {Number} transposeValue - Current transpose value (optional)
 * @param {Array} partsModule - Chord progressions data
 * @param {Object} options - Export options
 * @param {Boolean} options.includeChordProgressions - Whether to include chord progressions on the last page
 * @param {Boolean} options.includeSectionColors - Whether to include section background colors
 * @param {Boolean} options.minifyContent - Whether to show content in compact/collapsed form
 * @param {Boolean} options.pageBreakBeforeChords - Whether to add a page break before chord progressions
 * @returns {String} - HTML content for printing
 */
export const generatePrintContent = (songDataOrOptions, sections, transposeValue = 0, partsModule = [], options = {}) => {
  // Support both object parameter style and individual parameters
  let songData, finalSections, finalTranspose, finalPartsModule, finalOptions;
  
  if (typeof songDataOrOptions === 'object' && sections === undefined) {
    // Object parameter style: generatePrintContent({ songData, sections, ... })
    const params = songDataOrOptions;
    songData = params.songData;
    finalSections = params.sections;
    finalTranspose = params.transposeValue || 0;
    finalPartsModule = params.partsModule || [];
    finalOptions = {
      includeChordProgressions: params.includeChords,
      includeSectionColors: params.includeColors,
      includeBpm: params.includeBpm,
      ...params.options
    };
  } else {
    // Individual parameters style: generatePrintContent(songData, sections, ...)
    songData = songDataOrOptions;
    finalSections = sections;
    finalTranspose = transposeValue;
    finalPartsModule = partsModule;
    finalOptions = options;
  }
  const { 
    includeChordProgressions = true, 
    includeSectionColors = true, 
    minifyContent = false, 
    pageBreakBeforeChords = true, 
    includeLyrics = true, 
    includeLineup = true, 
    lineup = null,
    includeNotation = true,  // Add notation support
    showSection = true,
    showPart = true,
    showBars = true,
    showNotes = true,
    showLyricsColumn = true,  // Default to true
    showHeaders = false,  // Default to false for cleaner look
    rotateSection = false,  // Default to false for normal horizontal text
    showEnergy = true,  // Default to true to show energy bars
    showChordsColumn = true  // Default to true to show chords inline
  } = finalOptions;
  
  // Pre-calculate the max chord line width from partsModule for consistent column sizing
  // Uses ~6px per character in Inconsolata 10px
  const maxChordLineLength = showChordsColumn && finalPartsModule ? Math.max(
    0,
    ...finalPartsModule.map(p => {
      const chords = p.chords || '';
      const transposed = finalTranspose !== 0 ? getTransposedChords(chords, finalTranspose) : chords;
      return Math.max(...transposed.split('\n').map(line => line.length), 0);
    })
  ) : 0;
  const chordsColumnWidth = maxChordLineLength > 0 ? `${Math.ceil(maxChordLineLength * 5.5) + 2}px` : '0px';

  // Helper function to generate grid columns based on visibility
  const getGridColumns = () => {
    const columns = [];
    if (showSection) columns.push(rotateSection ? '36px' : '72px');
    if (showPart) columns.push('24px');
    if (showBars) columns.push('24px');
    if (showChordsColumn) columns.push(chordsColumnWidth);
    if (showLyricsColumn) columns.push('1fr'); // Lyrics column conditionally shown
    if (showNotes) columns.push('12.5%');
    columns.push('auto'); // Actions column placeholder
    return columns.join(' ');
  };

  const getPartGridColumns = () => {
    const columns = [];
    if (showPart) columns.push('24px');
    if (showBars) columns.push('24px');
    if (showChordsColumn) columns.push(chordsColumnWidth);
    if (showLyricsColumn) columns.push('1fr'); // Lyrics column conditionally shown
    if (showNotes) columns.push('12.5%');
    columns.push('auto'); // Actions column placeholder
    return columns.join(' ');
  };

  // Generate print content with options
  // Filter out placeholder text before exporting
  const processedSections = finalSections.map(section => ({
    ...section,
    parts: section.parts.map(part => ({
      ...part,
      // Don't export empty strings for lyrics and notes
      lyrics: part.lyrics || '',
      notes: part.notes || ''
    }))
  }));
  
  // Generate the print-friendly HTML
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${songData.title || 'Untitled'} - Band Sheet</title>
        <meta charset="utf-8">
        <link href="https://fonts.googleapis.com/css2?family=Inconsolata&display=swap" rel="stylesheet">
        <style>
          @media print {
            .page-break {
              page-break-before: always;
            }
            button {
              display: none;
            }
            .no-print {
              display: none;
            }
          }
          @media screen {
            .chord-page-header {
              display: none !important;
            }
            .chord-progressions {
              margin-top: 20px !important;
            }
          }
          
          /* Hide original chords column on small screens */
          @media screen and (max-width: 768px) {
            .chord-grid-header {
              grid-template-columns: minmax(40px, max-content) minmax(35px, max-content) 1fr !important;
            }
            .chord-grid-row {
              grid-template-columns: minmax(40px, max-content) minmax(35px, max-content) 1fr !important;
            }
            .original-chords {
              display: none !important;
            }
          }
          body {
            font-family: 'Inconsolata', 'Courier New', monospace;
            line-height: 1.15;
            color: #333;
            max-width: 900px;
            margin: 0 auto;
            padding: 5px 10px;
          }
          h1 {
            font-size: 16px;
            margin: 0 15px 0 0;
            display: inline-block;
          }
          .meta {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 10px;
            margin-bottom: 5px;
            font-size: 12px;
            color: #555;
          }
          /* Sheet container */
          .sheet-container {
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
            margin-top: 3px;
          }
          /* Sheet header row */
          .sheet-header {
            display: grid;
            grid-template-columns: ${getGridColumns()};
            gap: 4px;
            padding: 2px 8px;
            background-color: #f8f8f8;
            border-bottom: 1px solid #ddd;
            font-weight: bold;
            font-size: 11px;
          }
          /* Section container */
          .section-container {
            border-bottom: 1px solid #ddd;
            padding: 2px 0;
          }
          .section-container:last-child {
            border-bottom: none;
          }
          /* Flex row for section header + parts */
          .section-content {
            display: flex;
          }
          /* Energy bar - uses border instead of background for print compatibility */
          .energy-bar {
            height: 0;
            border-top: ${ENERGY_LINE_CONFIG.HEIGHT}px solid black;
          }
          /* Section header */
          .section-header {
            ${showSection ? (rotateSection ? 'width: 36px; min-width: 36px;' : 'width: 72px; min-width: 72px;') : 'display: none;'}
            padding: ${rotateSection ? '4px 2px' : '4px 6px'};
            border-right: 1px solid #ddd;
            display: ${showSection ? 'flex' : 'none'};
            flex-direction: column;
            justify-content: ${rotateSection ? 'center' : 'space-between'};
            ${rotateSection ? 'align-items: center;' : ''}
          }
          .section-name {
            font-weight: bold;
            font-size: 11px;
            ${rotateSection ? `
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transform: rotate(180deg);
            white-space: nowrap;
            max-height: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            ` : ''}
          }
          /* Energy indicator line (legacy class) */
          .energy-line {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background-color: black;
          }
          /* Parts container */
          .parts-container {
            flex: 1;
            ${!showSection ? 'width: 100%;' : ''}
            display: grid;
            grid-template-columns: ${getPartGridColumns()};
            gap: 0 4px;
            padding: 0 8px;
            align-items: center;
          }
          /* Part row - display:contents so all rows share the same grid */
          .part-row {
            display: contents;
          }
          /* Row separators: padding on each cell to create visual spacing */
          .part-row > div {
            padding-top: 2px;
            padding-bottom: 2px;
          }
          /* Column styles */
          .lyrics {
            white-space: pre-line;
            line-height: 1.15;
            font-size: 12px;
          }
          .notes {
            font-size: 10px;
            color: #666;
            line-height: 1.15;
          }
          .chords {
            font-family: 'Inconsolata', 'Courier New', monospace;
            font-size: 10px;
            white-space: pre-wrap;
            line-height: 1.15;
            color: #444;
          }
          @media print {
            body {
              padding: 0;
              margin: 0;
            }
          }
        </style>
      </head>
      <body>
        <div class="meta">
          <h1 style="display: flex; align-items: center; gap: 8px;">
            <svg
              class="no-print"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="cursor: pointer; flex-shrink: 0;"
              onclick="window.location.href = window.location.pathname;"
            >
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            <span>${songData.title || 'Untitled'}</span>
          </h1>
          ${songData.artist ? `<div><strong>Artist:</strong> ${songData.artist}</div>` : ''}
          ${songData.bpm ? `<div><strong>BPM:</strong> ${songData.bpm}</div>` : ''}
          ${finalTranspose !== 0 ? `<div><strong>Transposed:</strong> ${finalTranspose > 0 ? '+' + finalTranspose : finalTranspose} semitones</div>` : ''}
          ${songData.ownerName ? `<div><strong>By:</strong> ${songData.ownerName}</div>` : ''}
        </div>
        
        <div class="sheet-container">
          <!-- Sheet header -->
          ${includeLyrics ? `<div class="sheet-header">
            ${showSection ? '<div>Section</div>' : ''}
            ${minifyContent ? `
              ${showPart ? (showHeaders ? '<div>Parts</div>' : '<div></div>') : ''}
              ${showBars ? (showHeaders ? '<div>Bars</div>' : '<div></div>') : ''}
              ${showChordsColumn ? (showHeaders ? '<div>Chords</div>' : '<div></div>') : ''}
              ${showLyricsColumn ? '<div>First Line</div>' : ''}
              ${showNotes ? '<div>Notes</div>' : ''}
            ` : `
              ${showPart ? (showHeaders ? '<div>Part</div>' : '<div></div>') : ''}
              ${showBars ? (showHeaders ? '<div>Bars</div>' : '<div></div>') : ''}
              ${showChordsColumn ? (showHeaders ? '<div>Chords</div>' : '<div></div>') : ''}
              ${showLyricsColumn ? '<div>Lyrics</div>' : ''}
              ${showNotes ? '<div>Notes</div>' : ''}
            `}
            <div><!-- Actions placeholder --></div>
          </div>` : '<!-- Header excluded by lyrics=false parameter -->'}
          
          <!-- Sections -->
          <!-- Sections generated with minify setting: ${minifyContent ? 'enabled' : 'disabled'} -->
          ${includeLyrics ? processedSections.map((section, index) => {
            
            if (minifyContent) {
              // Collapsed section view
              const partsSummary = createPartsSummary(section.parts);
              const totalBars = getTotalBars(section.parts, finalPartsModule);
              const firstLine = getFirstTextLine(section.parts);
              const combinedNotes = getCombinedNotes(section.parts);
              
              return `
                <div class="section-container"${includeSectionColors && section.backgroundColor ? ` style="background-color: ${section.backgroundColor};"` : ''}>
                  <div class="section-content">
                    <!-- Section header -->
                    ${showSection ? `<div class="section-header">
                      <div class="section-name">${section.name}</div>
                    </div>` : ''}

                    <!-- Collapsed parts container -->
                    <div class="parts-container">
                      <div class="part-row">
                        ${showPart ? `<div style="font-size: 12px; font-weight: bold; line-height: 1.15;">${partsSummary}</div>` : ''}
                        ${showBars ? `<div style="font-size: 12px; font-weight: bold;">${totalBars}</div>` : ''}
                        ${showChordsColumn ? `<div class="chords"></div>` : ''}
                        ${showLyricsColumn ? `<div class="lyrics" style="font-style: italic;">${firstLine}</div>` : ''}
                        ${showNotes ? `<div class="notes">${combinedNotes}</div>` : ''}
                        <div><!-- No actions in print view --></div>
                      </div>
                    </div>
                  </div>
                  ${showEnergy && section.energy ? `<div class="energy-bar" style="width: ${getEnergyWidthForPdf(section.energy)};"></div>` : ''}
                </div>
              `;
            } else {
              // Normal section view
              return `
                <div class="section-container"${includeSectionColors && section.backgroundColor ? ` style="background-color: ${section.backgroundColor};"` : ''}>
                  <div class="section-content">
                    <!-- Section header -->
                    ${showSection ? `<div class="section-header">
                      <div class="section-name">${section.name}</div>
                    </div>` : ''}

                    <!-- Parts container -->
                    <div class="parts-container">
                      ${section.parts.map((part, pi) => `
                        <div class="part-row">
                          ${showPart ? `<div style="font-size: 12px; font-weight: bold;">${part.part}</div>` : ''}
                          ${showBars ? `<div style="font-size: 12px;">${getBarsForPart(part.part, finalPartsModule)}</div>` : ''}
                          ${showChordsColumn ? `<div class="chords">${getChordsForPart(part.part, finalPartsModule) ? getTransposedChords(getChordsForPart(part.part, finalPartsModule), finalTranspose) : ''}</div>` : ''}
                          ${showLyricsColumn ? `<div class="lyrics">${part.lyrics}</div>` : ''}
                          ${showNotes ? `<div class="notes">${part.notes || ''}</div>` : ''}
                          <div><!-- No actions in print view --></div>
                        </div>
                      `).join('')}
                    </div>
                  </div>
                  ${showEnergy && section.energy ? `<div class="energy-bar" style="width: ${getEnergyWidthForPdf(section.energy)};"></div>` : ''}
                </div>
              `;
            }
          }).join('') : '<!-- Sections excluded by lyrics=false parameter -->'}
        </div>
        
        <!-- Last Page: Chord Progressions -->
        <!-- Chord progressions: ${includeChordProgressions ? 'enabled' : 'disabled'}, data: ${finalPartsModule && finalPartsModule.length > 0 ? `${finalPartsModule.length} parts` : 'none'} -->
        ${includeChordProgressions && finalPartsModule && finalPartsModule.length > 0 ? `
        ${pageBreakBeforeChords ? '<div class="page-break"></div>' : ''}
        <!-- Header for chord progressions page -->
        ${pageBreakBeforeChords ? `<div class="chord-page-header" style="margin-bottom: 10px;">
          <h1 style="font-size: 14px; margin: 0 15px 0 0; display: inline-block;">${songData.title || 'Untitled'}</h1>
          ${songData.artist ? `<span style="font-size: 12px; color: #555; margin-right: 10px;"><strong>Artist:</strong> ${songData.artist}</span>` : ''}
          ${songData.leadVocalist ? `<span style="font-size: 12px; color: #555; margin-right: 10px;"><strong>Lead:</strong> ${songData.leadVocalist}</span>` : ''}
          ${songData.arranger ? `<span style="font-size: 12px; color: #555; margin-right: 10px;"><strong>Arr:</strong> ${songData.arranger}</span>` : ''}
          ${songData.bpm ? `<span style="font-size: 12px; color: #555; margin-right: 10px;"><strong>BPM:</strong> ${songData.bpm}</span>` : ''}
          ${finalTranspose !== 0 ? `<span style="font-size: 12px; color: #555;"><strong>Transposed:</strong> ${finalTranspose > 0 ? '+' + finalTranspose : finalTranspose} semitones</span>` : ''}
        </div>` : ''}
        <div class="chord-progressions" style="display: flex; flex-direction: column; ${pageBreakBeforeChords ? '' : 'margin-top: 20px;'}">
          <h2 style="font-size: 14px; margin-top: 0px; margin-bottom: 5px;">Parts</h2>

          <div style="border: 1px solid #ddd; border-radius: 4px; overflow: hidden;">
            <!-- Header row -->
            <div class="chord-grid-header" style="display: grid; grid-template-columns: minmax(30px, max-content) minmax(30px, max-content) 1fr; gap: 4px; padding: 3px 8px; background-color: #f8f8f8; border-bottom: 1px solid #ddd; font-weight: bold; font-size: 11px;">
              <div>Part</div>
              <div>Bars</div>
              <div>Chords</div>
            </div>

            <!-- Chord progression rows -->
            ${finalPartsModule.map(part => `
              <div class="chord-grid-row" style="display: grid; grid-template-columns: minmax(30px, max-content) minmax(30px, max-content) 1fr; gap: 4px; padding: 2px 8px; border-bottom: 1px solid #eee; font-size: 12px;">
                <div style="font-weight: bold;">${part.part}</div>
                <div>${part.bars}</div>
                <div style="white-space: pre;">${part.chords ? getTransposedChords(part.chords, finalTranspose) : ''}</div>
              </div>
            `).join('')}
          </div>
        </div>
        ` : ''}
        
        <!-- Notation Placeholder -->
        <div id="notation-placeholder"></div>
        
        <!-- Band Lineup Section -->
        <!-- Debug: includeLineup=${includeLineup}, lineup=${!!lineup}, lineups count=${lineup?.length || 0} -->
        ${includeLineup && lineup && lineup.length > 0 ? `
        ${pageBreakBeforeChords ? '<div class="page-break"></div>' : ''}
        <div class="lineup-members" style="margin-top: ${pageBreakBeforeChords ? '20px' : '40px'}; margin-bottom: 20px;">
          <h2 style="font-size: 18px; margin-bottom: 15px; color: #333;">Lineups</h2>
          ${lineup.map(l => `
            <div style="margin-bottom: 20px;">
              <h3 style="font-size: 14px; margin-bottom: 10px; color: #555;">${l.bandName}${l.lineupName ? ` - ${l.lineupName}` : ''}</h3>
              <div style="border: 1px solid #ddd; border-radius: 4px; overflow: hidden;">
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0;">
                  ${l.members && l.members.length > 0 ? l.members.map((member, index) => `
                    <div style="padding: 8px 12px; border-bottom: 1px solid #eee; ${index % 2 === 1 ? 'border-left: 1px solid #eee;' : ''} ${index >= l.members.length - 2 ? 'border-bottom: none;' : ''}">
                      <div style="color: #333;"><strong>${member.name}:</strong> <em>${member.instrument}</em></div>
                    </div>
                  `).join('') : '<div style="padding: 8px 12px; color: #999;">No members defined</div>'}
                </div>
              </div>
            </div>
          `).join('')}
        </div>
        ` : ''}
      </body>
    </html>
  `;
  
};

/**
 * Generate and open a print-friendly version in a new tab
 * @param {Object} songData - Contains title, artist, bpm
 * @param {Array} sections - All sections with their parts for the sheet
 * @param {Number} transposeValue - Current transpose value (optional)
 * @param {Array} partsModule - Chord progressions data
 * @param {Object} options - Export options
 * @param {Boolean} options.includeChordProgressions - Whether to include chord progressions on the last page
 * @param {Boolean} options.includeSectionColors - Whether to include section background colors
 * @param {Boolean} options.minifyContent - Whether to show content in compact/collapsed form
 * @param {Boolean} options.pageBreakBeforeChords - Whether to add a page break before chord progressions
 */
export const exportToPDF = (songData, sections, transposeValue = 0, partsModule = [], options = {}) => {
  // Create a new window/tab
  const printWindow = window.open('', '_blank');
  
  // Generate the print-friendly HTML
  const printContent = generatePrintContent(songData, sections, transposeValue, partsModule, options);
  
  // Write the content to the new window
  printWindow.document.write(printContent);
  printWindow.document.close();
};

/**
 * Load a sheet by ID and return the print content
 * @param {string} sheetId - ID of the sheet to print
 * @param {Object} options - Export options
 * @returns {Promise<Object>} - Object containing print content and sheet data
 */
export const getPrintContentBySheetId = async (sheetId, options = {}) => {
  try {
    // Validate sheetId
    if (!sheetId || sheetId === 'undefined' || sheetId === 'null') {
      throw new Error('Invalid sheet ID provided');
    }
    const sheet = await getSheetById(sheetId);
    if (!sheet) {
      throw new Error('Sheet not found');
    }
    
    const songData = {
      title: sheet.title || '',
      artist: sheet.artist || '',
      bpm: sheet.bpm || '',
      leadVocalist: sheet.leadVocalist || sheet.lead || '',
      arranger: sheet.arranger || '',
      notations: sheet.notations || [],  // Changed to array for multiple notations
      ownerName: sheet.owner?.username || ''
    };
    
    const sections = sheet.sections || [];
    const partsModule = sheet.partsModule || [];
    const transposeValue = sheet.transposeValue || 0;
    
    // Use provided options with defaults
    const finalOptions = {
      ...options
    };
    
    const printContent = generatePrintContent(songData, sections, transposeValue, partsModule, finalOptions);
    
    return {
      printContent,
      sheet
    };
  } catch (error) {
    logger.error('ExportService', 'Error generating print content:', error);
    throw error;
  }
};

/**
 * Export a single sheet to a JSON file
 * @param {string} sheetId - ID of the sheet to export
 * @returns {Promise<Object>} Promise that resolves when export is complete
 */
export const exportSingleSheet = async (sheetId) => {
  try {
    // Get the sheet from storage
    const sheet = await getSheetById(sheetId);
    
    if (!sheet) {
      throw new Error(`Sheet with ID ${sheetId} not found`);
    }
    
    // Export the sheet directly (same format as localStorage)
    // This makes it consistent with the localStorage format
    const jsonData = JSON.stringify(sheet, null, 2);
    
    // Create a blob and download
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create a safe filename based on the sheet title
    const safeTitle = sheet.title.replace(/[^a-z0-9]/gi, '_');
    const filename = `${safeTitle}_${sheet._id || sheet.id}.json`;
    
    // Create a download link and trigger it
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    return { success: true, message: `Sheet "${sheet.title}" exported successfully` };
  } catch (error) {
    logger.error('ExportService', 'Error exporting sheet:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export all sheets to a JSON file
 * @returns {Promise<Object>} Promise that resolves when export is complete
 */
export const exportSheets = async () => {
  try {
    // Get all sheets from API or localStorage without triggering UI updates
    const sheetList = await getAllSheets(true, true); // sortByNewest=true, skipUIRefresh=true
    
    if (!sheetList || sheetList.length === 0) {
      throw new Error('No sheets found to export');
    }
    
    // Fetch the complete data for each sheet
    const completeSheets = [];
    const isAuthenticated = checkAuth();
    
    // For each sheet in the list, get its complete data
    for (const sheetInfo of sheetList) {
      try {
        let completeSheet;
        
        // Use _id or id, whichever is available, and validate it's not undefined
        const sheetId = sheetInfo._id || sheetInfo.id;
        if (!sheetId || sheetId === 'undefined' || sheetId === 'null') {
          logger.warn('ExportService', `Skipping sheet with invalid ID: ${sheetId}`);
          continue;
        }
        
        if (isAuthenticated && API_URL) {
          // Get complete sheet data from API
          try {
            const response = await fetchWithAuth(`${API_URL}/sheets/${sheetId}`);
            completeSheet = response.data;
          } catch (error) {
            logger.error('ExportService', `Error fetching complete data for sheet ${sheetId}:`, error);
            // Try localStorage as fallback
            const localData = localStorage.getItem(`sheet_${sheetId}`);
            if (localData) {
              completeSheet = JSON.parse(localData);
            }
          }
        } else {
          // Get from localStorage
          const localData = localStorage.getItem(`sheet_${sheetId}`);
          if (localData) {
            completeSheet = JSON.parse(localData);
          }
        }
        
        if (completeSheet) {
          completeSheets.push(completeSheet);
        }
      } catch (error) {
        const sheetId = sheetInfo._id || sheetInfo.id || 'unknown';
        logger.error('ExportService', `Error processing sheet ${sheetId}:`, error);
      }
    }
    
    if (completeSheets.length === 0) {
      throw new Error('Failed to retrieve complete data for any sheets');
    }
    
    // Create export object with metadata
    const exportData = {
      exportDate: new Date(),
      sheetsCount: completeSheets.length,
      sheets: completeSheets
    };
    
    // Convert to JSON string
    const jsonData = JSON.stringify(exportData, null, 2);
    
    // Create blob and download link
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create download link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `band_sheets_export_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return {
      success: true,
      message: `${completeSheets.length} sheets exported successfully`
    };
  } catch (error) {
    logger.error('ExportService', 'Export failed:', error);
    throw error;
  }
};

/**
 * Generate and open a combined print-friendly version of multiple sheets in a new tab
 * @param {Array} sheets - Array of sheet objects to include in the PDF
 * @param {Object} setlistInfo - Information about the setlist (name, description)
 * @param {Object} options - Export options
 * @returns {Promise<void>}
 */
export const exportMultipleSheetsToPDF = async (sheets, setlistInfo, options = {}) => {
  if (!sheets || sheets.length === 0) {
    throw new Error('No sheets provided for export');
  }
  
  // Extract column visibility options with defaults
  const {
    showSection = true,
    showPart = true,
    showBars = true,
    showNotes = true,
    showLyricsColumn = true,  // Default to true
    // showHeaders = false,  // Not used in bulk export yet
    rotateSection = false  // Default to false for normal horizontal text
  } = options;
  
  // Helper functions for grid columns (same as in generatePrintContent)
  const getGridColumns = () => {
    const columns = [];
    if (showSection) columns.push(rotateSection ? '40px' : '80px');
    if (showPart) columns.push('30px');  // Reduced from 48px
    if (showBars) columns.push('30px');  // Reduced from 48px
    if (showLyricsColumn) columns.push('1fr'); // Lyrics column conditionally shown
    if (showNotes) columns.push('12.5%');
    columns.push('auto'); // Actions column placeholder
    return columns.join(' ');
  };
  
  const getPartGridColumns = () => {
    const columns = [];
    if (showPart) columns.push('30px');  // Reduced from 48px
    if (showBars) columns.push('30px');  // Reduced from 48px
    if (showLyricsColumn) columns.push('1fr'); // Lyrics column conditionally shown
    if (showNotes) columns.push('12.5%');
    columns.push('auto'); // Actions column placeholder
    return columns.join(' ');
  };

  // Create a new window/tab
  const printWindow = window.open('', '_blank');
  
  // Start with a basic HTML structure
  let combinedContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${setlistInfo.name || 'Setlist'} - Band Sheets</title>
        <meta charset="utf-8">
        <link href="https://fonts.googleapis.com/css2?family=Inconsolata&display=swap" rel="stylesheet">
        <style>
          @media print {
            .page-break {
              page-break-before: always;
            }
            .no-print {
              display: none;
            }
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.4;
            color: #333;
            max-width: 900px;
            margin: 0 auto;
            padding: 10px;
          }
          .setlist-header {
            text-align: center;
            margin-bottom: 20px;
          }
          .setlist-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .setlist-description {
            font-size: 16px;
            color: #666;
            margin-bottom: 15px;
          }
          h1 {
            font-size: 20px;
            margin: 0 20px 0 0;
            display: inline-block;
          }
          .meta {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 15px;
            margin-bottom: 10px;
            font-size: 14px;
            color: #555;
          }
          /* Sheet container */
          .sheet-container {
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
            margin-top: 5px;
            margin-bottom: 50px;
          }
          
          /* Sheet separator - visual indicator between sheets */
          .sheet-separator {
            border-bottom: 2px dashed #ccc;
            margin: 30px 0 60px 0;
            padding-bottom: 20px;
          }
          /* Sheet header row */
          .sheet-header {
            display: grid;
            grid-template-columns: ${getGridColumns()};
            gap: 10px;
            padding: 5px 16px;
            background-color: #f8f8f8;
            border-bottom: 1px solid #ddd;
            font-weight: bold;
            font-size: 14px;
          }
          /* Section container */
          .section-container {
            display: flex;
            border-bottom: 1px solid #ddd;
            position: relative;
          }
          .section-container:last-child {
            border-bottom: none;
          }
          /* Section header */
          .section-header {
            ${showSection ? (rotateSection ? 'width: 40px; min-width: 40px;' : 'width: 80px; min-width: 80px;') : 'display: none;'}
            padding: ${rotateSection ? '8px 4px' : '8px 8px'};
            border-right: 1px solid #ddd;
            display: ${showSection ? 'flex' : 'none'};
            flex-direction: column;
            justify-content: ${rotateSection ? 'center' : 'space-between'};
            ${rotateSection ? 'align-items: center;' : ''}
            position: relative;
          }
          .section-name {
            font-weight: bold;
            font-size: 13px;
            ${rotateSection ? `
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transform: rotate(180deg);
            white-space: nowrap;
            max-height: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            ` : ''}
          }
          /* Energy indicator line */
          .energy-line {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background-color: black;
          }
          /* Parts container */
          .parts-container {
            flex: 1;
            ${!showSection ? 'width: 100%;' : ''}
          }
          /* Part row */
          .part-row {
            display: grid;
            grid-template-columns: ${getPartGridColumns()};
            gap: 10px;
            padding: 5px 16px;
            border-bottom: 1px solid #eee;
            align-items: center;
          }
          .part-row:last-child {
            border-bottom: none;
          }
          /* Column styles */
          .lyrics {
            white-space: pre-line;
            line-height: 1.3;
            font-family: 'Inconsolata', monospace;
          }
          .notes {
            font-size: 11px;
            color: #666;
            line-height: 1.2;
          }
        </style>
      </head>
      <body>
        <div class="setlist-header">
          <div class="setlist-title">${setlistInfo.name || 'Untitled Setlist'}</div>
          ${setlistInfo.description ? `<div class="setlist-description">${setlistInfo.description}</div>` : ''}
        </div>
  `;

  // Process each sheet
  for (let i = 0; i < sheets.length; i++) {
    const sheet = sheets[i];
    if (!sheet) continue;

    try {
      // Extract data needed for print content
      const songData = {
        title: sheet.title || '',
        artist: sheet.artist || '',
        bpm: sheet.bpm || ''
      };
      
      const sections = sheet.sections || [];
      const partsModule = sheet.partsModule || [];
      const transposeValue = sheet.transposeValue || 0;
      
      // Generate content without the HTML wrapper (we'll use our own)
      const { printContent } = await generateSheetContent(songData, sections, transposeValue, partsModule, options);
      
      // Add a page break before each sheet (except the first one)
      if (i > 0) {
        combinedContent += '<div class="page-break"></div>';
        // Add setlist header to each new page
        combinedContent += `
          <div class="setlist-header" style="margin-bottom: 20px;">
            <div style="font-size: 16px; font-weight: bold; margin-bottom: 5px;">${setlistInfo.name || 'Untitled Setlist'}</div>
            ${setlistInfo.description ? `<div style="font-size: 14px; color: #666;">${setlistInfo.description}</div>` : ''}
          </div>
        `;
      }
      
      // Add the sheet content
      combinedContent += printContent;
      
      // Add a visual separator after each sheet (except the last one)
      if (i < sheets.length - 1) {
        combinedContent += '<div class="sheet-separator"></div><div class="no-print">Next sheet: ' + (sheets[i+1].title || 'Untitled') + '</div>';
      }
    } catch (error) {
      logger.error('ExportService', `Error processing sheet ${sheet._id || sheet.id || i}:`, error);
      // Continue with other sheets even if one fails
    }
  }

  // Close the HTML document
  combinedContent += `
      </body>
    </html>
  `;
  
  // Write the content to the new window
  printWindow.document.write(combinedContent);
  printWindow.document.close();
};

/**
 * Generate content for a single sheet without the full HTML wrapper
 * Helper function for exportMultipleSheetsToPDF
 */
const generateSheetContent = (songData, sections, transposeValue = 0, partsModule = [], options = {}) => {
  const { includeChordProgressions = true, includeSectionColors = true, minifyContent = false, pageBreakBeforeChords = true } = options;
  // Filter out placeholder text before exporting
  const processedSections = sections.map(section => ({
    ...section,
    parts: section.parts.map(part => ({
      ...part,
      // Don't export empty strings for lyrics and notes
      lyrics: part.lyrics || '',
      notes: part.notes || ''
    }))
  }));
  
  // Generate just the sheet content without the HTML document wrapper
  const printContent = `
    <div class="meta">
      <h1>${songData.title || 'Untitled'}</h1>
      ${songData.artist ? `<div><strong>Artist:</strong> ${songData.artist}</div>` : ''}
      ${songData.bpm ? `<div><strong>BPM:</strong> ${songData.bpm}</div>` : ''}
      ${transposeValue !== 0 ? `<div><strong>Transposed:</strong> ${transposeValue > 0 ? '+' + transposeValue : transposeValue} semitones</div>` : ''}
    </div>
    
    <div class="sheet-container" style="padding-bottom: 20px;">
      <!-- Sheet header -->
      <div class="sheet-header">
        <div>Section</div>
        ${minifyContent ? `
          <div>Parts</div>
          <div>Bars</div>
          <div>First Line</div>
          <div>Notes</div>
        ` : `
          <div>Part</div>
          <div>Chords</div>
          <div>Lyrics</div>
          <div>Key Parts</div>
          <div>Notes</div>
        `}
      </div>

      <!-- Sections -->
      ${processedSections.map(section => {
        if (minifyContent) {
          // Collapsed section view
          const partsSummary = createPartsSummary(section.parts);
          const totalBars = getTotalBars(section.parts, partsModule);
          const firstLine = getFirstTextLine(section.parts);
          const combinedNotes = getCombinedNotes(section.parts);
          
          return `
            <div class="section-container" ${includeSectionColors && section.backgroundColor ? `style="background-color: ${section.backgroundColor}"` : ''}>
              <div class="section-header">
                <div class="section-name">${section.name || ''}</div>
                ${section.energyLevel ? `<div class="energy-line" style="width: ${getEnergyWidthForPdf(section.energyLevel)}"></div>` : ''}
              </div>
              <div class="parts-container">
                <div class="part-row">
                  <div style="font-size: 12px; line-height: 1.2;">${partsSummary}</div>
                  <div style="font-weight: bold;">${totalBars}</div>
                  <div class="lyrics" style="font-style: italic;">${firstLine}</div>
                  <div class="notes">${combinedNotes}</div>
                </div>
              </div>
            </div>
          `;
        } else {
          // Normal section view
          return `
            <div class="section-container" ${includeSectionColors && section.backgroundColor ? `style="background-color: ${section.backgroundColor}"` : ''}>
              <div class="section-header">
                <div class="section-name">${section.name || ''}</div>
                ${section.energyLevel ? `<div class="energy-line" style="width: ${getEnergyWidthForPdf(section.energyLevel)}"></div>` : ''}
              </div>
              <div class="parts-container">
                ${section.parts.map(part => `
                  <div class="part-row">
                    <div>${part.name || ''}</div>
                    <div style="font-family: 'Inconsolata', monospace;">${transposeValue !== 0 ? getTransposedChords(part.chords, transposeValue) : (part.chords || '')}</div>
                    <div class="lyrics">${part.lyrics || ''}</div>
                    <div>${part.keyParts || ''}</div>
                    <div class="notes">${part.notes || ''}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        }
      }).join('')}
    </div>
  `;
  
  // Add chord progressions on page 2 if requested
  let chordProgressionsContent = '';
  if (includeChordProgressions && partsModule && partsModule.length > 0) {
    // Only include parts with chord progressions
    const partsWithProgressions = partsModule.filter(part => part.chords && part.chords.trim().length > 0);
    
    if (partsWithProgressions.length > 0) {
      chordProgressionsContent = `
        ${pageBreakBeforeChords ? '<div class="page-break"></div>' : ''}
        <div style="${pageBreakBeforeChords ? '' : 'margin-top: 40px;'}">
        <h2>Chord Progressions</h2>
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-family: 'Inconsolata', monospace;">
          <thead>
            <tr style="background-color: #f8f8f8;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; width: auto; white-space: nowrap;">Part</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Chords${transposeValue !== 0 ? ` (${transposeValue > 0 ? '+' + transposeValue : transposeValue})` : ''}</th>
            </tr>
          </thead>
          <tbody>
            ${partsWithProgressions.map(part => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px; white-space: nowrap;">${part.name || ''}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${transposeValue !== 0 ? getTransposedChords(part.chords, transposeValue) : (part.chords || '')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        </div>
      `;
    }
  }
  
  return { printContent: printContent + chordProgressionsContent, processedSections };
};

/**
 * Export a single sheet to PDF by its ID
 * @param {string} sheetId - ID of the sheet to export
 * @param {Object} options - Export options
 * @returns {Promise<Object>} - Object with success/error info
 */
export const exportSheetToPDF = async (sheetId, options = {}) => {
  try {
    // Validate sheetId
    if (!sheetId || sheetId === 'undefined' || sheetId === 'null') {
      throw new Error('Invalid sheet ID provided');
    }
    const { printContent } = await getPrintContentBySheetId(sheetId, options);
    
    // Create a new window/tab
    const printWindow = window.open('', '_blank');
    
    // Write the content to the new window
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    return { success: true };
  } catch (error) {
    logger.error('ExportService', 'Error exporting sheet to PDF:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export all sheets in a setlist as a single PDF
 * @param {string} setlistId - ID of the setlist to export
 * @param {Object} options - Export options
 * @returns {Promise<Object>} - Object with success/error info
 */
export const exportSetlistToPDF = async (setlistId, options = {}) => {
  try {
    // Import the function dynamically to avoid circular dependencies
    const { getSetlistById } = await import('./SetlistService');
    
    // Get the setlist data
    const setlist = await getSetlistById(setlistId);
    if (!setlist) {
      throw new Error('Setlist not found');
    }
    
    if (!setlist.sheets || setlist.sheets.length === 0) {
      throw new Error('This setlist does not contain any sheets');
    }
    
    // Load the full data for each sheet
    const fullSheets = [];
    for (const sheetInfo of setlist.sheets) {
      try {
        const sheet = await getSheetById(sheetInfo._id || sheetInfo.id);
        if (sheet) {
          fullSheets.push(sheet);
        }
      } catch (error) {
        logger.error('ExportService', `Error loading sheet ${sheetInfo.id}:`, error);
        // Continue with other sheets
      }
    }
    
    if (fullSheets.length === 0) {
      throw new Error('Could not load any sheets in this setlist');
    }
    
    // Create setlist info object
    const setlistInfo = {
      name: setlist.name || 'Untitled Setlist',
      description: setlist.description || ''
    };
    
    // Generate the PDF with all sheets
    await exportMultipleSheetsToPDF(fullSheets, setlistInfo, options);
    
    return { success: true, message: `${fullSheets.length} sheets exported successfully` };
  } catch (error) {
    logger.error('ExportService', 'Error exporting setlist to PDF:', error);
    return { success: false, error: error.message };
  }
};

// Named exports only
// No default export to avoid ESLint warning
